/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 DOM 이벤트 바인딩/라우팅을 정의한다.
 *
 * @file /src/rootin/core/Events.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 4.
 */
import { Editor } from '@rootin/core/Editor';
import { Block, RootinBlock } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Container } from '@rootin/elements/container/Container';
import { Paragraph } from '@rootin/blocks/paragraph/Paragraph';
import { Text } from '@rootin/elements/text/Text';
import { FileDrag } from '@rootin/events/FileDrag';
import { SelectionChange } from '@rootin/events/SelectionChange';
import { Composition } from '@rootin/events/Composition';
import { CopyPaste } from '@rootin/events/CopyPaste';
import { Language } from '@utils/Language';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';

export class Events {
    /**
     * 파일드래그 이벤트 클래스
     */
    fileDrag: FileDrag;

    /**
     * 선택영역 변경 이벤트 클래스
     */
    selectionChange: SelectionChange;

    /**
     * IME 조합 이벤트 클래스
     */
    composition: Composition;

    /**
     * 복사/붙여넣기 이벤트 클래스
     */
    copyPaste: CopyPaste;

    /**
     * 파괴적 선택영역이 감지되어, 네이티브가 스스로 지운 뒤(= `input` 이벤트) 뒷정리가 필요한
     * 상태를 들고 있는다. `null`이면 뒷정리 대기 중이 아니라는 뜻.
     */
    #pendingDestructiveCleanup: { containers: Container[]; selectedBlockIds: string[] } | null = null;

    /**
     * 에디터의 DOM 이벤트 바인딩/라우팅을 정의한다.
     *
     * @param editor - 이벤트를 정의할 에디터
     */
    constructor(public editor: Editor) {
        this.fileDrag = new FileDrag(editor);
        this.selectionChange = new SelectionChange(editor);
        this.composition = new Composition(editor);
        this.copyPaste = new CopyPaste(editor);
    }

    /**
     * 에디터 DOM 에 이벤트 리스너를 바인딩한다.
     */
    listen(): void {
        const $container = this.editor.$getContainer();

        // $textarea(JSON 모드)는 디자인 구조상 $container 안으로 들어와 있어서, 여기서부터
        // 바인딩하는 모든 리스너(이 파일 및 Composition/FileDrag/CopyPaste)의 버블링 경로를
        // 그대로 탄다 — 하지만 textarea 는 rootin 의 편집 대상이 아니라 순수 네이티브
        // textarea 이므로 이 리스너들이 개입하면 안 된다. 아래에서 이 등록들보다 먼저(=같은
        // $container 버블 단계 리스너 중 가장 먼저) 걸어두고 `stopImmediatePropagation()`으로
        // 같은 노드의 나머지 리스너만 끊는다 — target(textarea) 자신에게 직접 붙은 리스너(예:
        // JSON 동기화용 `$textarea.on('input', ...)`, Editor.ts 참고)는 버블 단계가 시작되기
        // 전(= target 단계)에 이미 끝나있으므로 영향받지 않는다(실측 확인). `document`에 직접
        // 바인딩되는 `mouseup`/`selectionchange`(SelectionChange.ts)는 이 노드를 거치지 않거나
        // target 이 늘 document 라 여기서 못 거르므로 그쪽에서 따로 막는다.
        for (const name of [
            'focusin',
            'focusout',
            'beforeinput',
            'input',
            'keydown',
            'click',
            'mousedown',
            'compositionstart',
            'compositionend',
            'copy',
            'paste',
            'dragenter',
            'dragleave',
            'drop',
        ] as const) {
            $container.on(name, (_$container, e) => {
                if (e.target === this.editor.$textarea.getEl()) {
                    e.stopImmediatePropagation();
                }
            });
        }

        this.composition.listen($container);

        // SelectionChange 가 잡아내지 못할 수 있는 포커스이동 가드
        $container.on('focusin', (_, e) => {
            this.onFocus(e);
        });
        $container.on('focusout', (_, e) => {
            this.onBlur(e);
        });

        // 네이티브 히스토리 기능 차단
        $container.on('beforeinput', (_$container, e) => {
            if (e.inputType === 'historyUndo' || e.inputType === 'historyRedo') {
                e.preventDefault();
                return;
            }

            // 파괴적 병합 — delete/cut/insert/조합 가리지 않고 네이티브를 막지 않는다. 어차피
            // `insertCompositionText`는 non-cancelable이라 `preventDefault()`가 안 먹히고,
            // 나머지(delete/insert)도 네이티브의 raw 삭제가 우리가 직접 계산하는 것보다 더
            // 정확하다(형제 블록 병합 등은 contenteditable의 기본동작이니까). 지금 상태만
            // 캡처해두고, 네이티브가 스스로 지운 뒤(`input`)에 뒷정리한다.
            if (this.editor.selection.isDestructiveSelection() === true) {
                this.#prepareDestructiveCleanup();
            }
        });

        // 파괴적 선택영역을 네이티브가 스스로 지운 뒤 뒷정리한다.
        $container.on('input', () => {
            if (this.#pendingDestructiveCleanup === null) {
                return;
            }

            const pending = this.#pendingDestructiveCleanup;
            this.#pendingDestructiveCleanup = null;
            this.#finishDestructiveCleanup(pending);
        });

        $container.on('keydown', (_$container, e) => {
            // 히스토리 처리
            if ((e.metaKey === true || e.ctrlKey === true) && e.key.toLowerCase() === 'z') {
                if (e.isComposing === true) {
                    return;
                }
                e.preventDefault();
                if (e.shiftKey === true) {
                    this.editor.history.redo();
                } else {
                    this.editor.history.undo();
                }
                return;
            }

            // 선택에 필요한 키다운 이벤트를 처리한다.
            if (this.selectionChange.onKeydown(e) === true) {
                return;
            }

            // 실제 편집 가능한 지점내의 이벤트를 처리한다.
            if (
                this.editor.root.body
                    .$getElement()
                    .getEl()
                    .contains(e.target as Node) === false
            ) {
                return;
            }

            // 탭이동을 caret.move() 함수에 위임한다.
            if (e.key === 'Tab') {
                if (this.editor.selection.isDestructiveSelection() === true) {
                    e.preventDefault();
                    return;
                }

                if (e.isComposing === true) {
                    this.composition.pendingAction = () =>
                        this.editor.caret.move(e.shiftKey === true ? 'backward' : 'forward');
                    return;
                }
                e.preventDefault();
                this.editor.caret.move(e.shiftKey === true ? 'backward' : 'forward');
                return;
            }

            // Enter 키 처리를 엘리먼트에 위임한다.
            if (e.key === 'Enter') {
                if (this.editor.selection.isDestructiveSelection() === true) {
                    e.preventDefault();
                    return;
                }

                if (e.isComposing === true) {
                    this.composition.pendingAction = () => this.onEnter(e.shiftKey);
                    return;
                }

                e.preventDefault();
                this.onEnter(e.shiftKey);
                return;
            }

            // Backspace/Delete 가 엘리먼트 시작/끝 경계에 있으면 인라인 형제 블록과 병합한다.
            if (e.key === 'Backspace' || e.key === 'Delete') {
                if (
                    this.editor.selection.isDestructiveSelection() === true ||
                    e.metaKey === true ||
                    e.ctrlKey === true ||
                    e.altKey === true ||
                    e.isComposing === true
                ) {
                    return;
                }

                if (this.onDeleteBoundary(e.key as 'Backspace' | 'Delete') === true) {
                    e.preventDefault();
                    return;
                }

                // 여기까지 왔으면 네이티브가 선택영역을 그대로 지우게 둔다는 뜻이다 — 근데
                // 마우스 드래그로 선택한 경우 activeElement가 이 텍스트 엘리먼트가 아니라
                // 조상 컨테이너로 가 있을 수 있어(실측 확인) beforeinput/input이 엉뚱한
                // target으로 뜨고 이 엘리먼트 자신의 리스너를 못 타는 경우가 있다 — 편집이
                // 끝난 다음 프레임에 실제로 탔는지 확인해서, 못 탔으면 우리가 직접 동기화한다.
                const range = this.editor.selection.getRange();
                const startElement = range !== null ? this.editor.focus.resolveElement(range.startContainer) : null;
                const block = startElement?.block ?? null;
                if (block !== null && block.getKind() === 'inline') {
                    const textElement = Html.get('rootin-element[data-type="text"]', block.$getBlock())?.getData(
                        'element'
                    ) as Text | undefined;
                    if (textElement !== undefined) {
                        textElement.expectEdit();
                        requestAnimationFrame(() => textElement.checkSynced());
                    }
                }
                return;
            }

            const ARROW_DIRECTIONS: { [key: string]: 'up' | 'down' | 'left' | 'right' } = {
                ArrowUp: 'up',
                ArrowDown: 'down',
                ArrowLeft: 'left',
                ArrowRight: 'right',
            };
            const direction = ARROW_DIRECTIONS[e.key];
            if (direction !== undefined) {
                // 단어/문서 단위 이동은 네이티브에 맡긴다.
                if (e.metaKey === true || e.ctrlKey === true || e.altKey === true || e.isComposing === true) {
                    return;
                }

                // 선택영역이 존재된 상태에서 shift 없이 이동시 선택을 풀고 경계로 이동하도록 네이티브에 맡긴다.
                const range = this.editor.selection.getRange();
                if (range === null || (e.shiftKey === false && range.collapsed === false)) {
                    return;
                }

                if (this.editor.caret.move(direction)) {
                    e.preventDefault();
                }
            }
        });

        $container.on('click', (_, e) => {
            const $target = Html.el(e.target as EventTarget) ?? null;
            if ($target === null) {
                return;
            }

            const $marker = $target.getParents('[data-action=add]') ?? null;
            if ($marker === null) {
                return;
            }

            // 마커 자신이 버튼이 아니면(래퍼 div 등) 그 안의 버튼 위에서 클릭이 일어났을 때만
            // 반응한다 — 래퍼의 빈 여백을 눌러도 반응하면 안 된다(Images.ts#createMarker 규칙 참고).
            if ($marker.is('button') === false) {
                const $button = $target.getParents('button');
                if ($button === null || $marker.getEl().contains($button.getEl()) === false) {
                    return;
                }
            }

            this.onAddMarkerClick($marker);
        });

        // 마우스 드래그로 블록 경계를 넘는 선택확장을 위한 이벤트처리
        $container.on('mousedown', (_$container, e) => {
            if (e.button !== 0) {
                return;
            }

            // 트리플클릭(문단 전체선택)은 네이티브가 선택범위를 다음 블록 경계까지 넘겨버려서
            // (실측 확인: endContainer가 다음 블록의 엘리먼트) 파괴적 선택 뒷정리가 이를
            // 커버하지 못하는 문제가 있다 — 일단 막는다.
            if (e.detail >= 3) {
                e.preventDefault();
                return;
            }

            // 컨테이너 빈 여백 클릭 시 가장 가까운 블록을 찾기 위한 지점 기억
            this.editor.focus.rememberPoint(e.clientX, e.clientY);

            // 텍스트 엘리먼트 위 드래그(제스처 시작) 판정은 SelectionChange 에 위임한다.
            if (this.selectionChange.onMouseDown(e) === true) {
                return;
            }

            // 텍스트 엘리먼트가 아닌 블록에 마우스를 클릭한 경우 해당 블록에 포커스를 지정한다.
            const element = this.editor.focus.resolveElement(e.target);
            const block = element?.block ?? this.editor.focus.resolveBlock(e.target);
            if (block !== null && block.getKind() === 'atomic') {
                // 네이티브 기본동작(가장 가까운 편집가능 지점으로 캐럿을 옮기려는 시도)이 우리가
                // 방금 옮긴 anchor 포커스를 다시 덮어써버리므로 막는다(실측 확인).
                e.preventDefault();
                block.focus(null);
            }
        });

        this.fileDrag.listen($container);
        this.selectionChange.listen($container);
        this.copyPaste.listen($container);

        // @todo contextmenu(→ this.onMenu(e)) 라우팅
    }

    /**
     * 블록 추가버튼을 클릭했을 때, 컨테이너의 addHandler 를 실행하거나, 기본 블록을 추가한다.
     *
     * @param $marker - 클릭된 추가버튼
     */
    async onAddMarkerClick($marker: Dom): Promise<void> {
        const container = $marker.getParents('rootin-element[data-type=container]')?.getData('element') as Container;
        if (container === undefined) {
            return;
        }

        if (container.addHandler !== null) {
            await container.addHandler(container, $marker);
            return;
        }

        const block = container.createDefaultBlock();
        if (block === null) {
            return;
        }

        await container.insertBlock(block, $marker);
    }

    /**
     * Enter 동작을 수행한다.
     *
     * @param shift - Shift 동반 여부
     */
    onEnter(shift: boolean): void {
        if (shift === true) {
            this.editor.caret.insertLineBreak();
            return;
        }

        const element = this.editor.focus.getElement();
        if (element !== null) {
            element.onEnter();
        } else {
            this.editor.focus.getBlock().onEnter(null);
        }
    }

    /**
     * 캐럿이 엘리먼트의 시작과 끝 경계에 있을 때, 삭제 이벤트를 처리한다.
     *
     * @param key - 'Backspace' | 'Delete'
     * @returns 처리(병합/캐럿이동)했으면 true(preventDefault 대상)
     */
    onDeleteBoundary(key: 'Backspace' | 'Delete'): boolean {
        const element = this.editor.focus.getElement();
        if (element === null) {
            return false;
        }

        const offsets = this.editor.selection.getOffsets(element);
        if (offsets === null || offsets.start !== offsets.end) {
            return false;
        }

        const elements = this.editor.selection.getElements();
        const index = elements.indexOf(element);

        // 무엇보다 먼저 — 내(현재 블록)가 비어있으면 이웃을 따질 것도 없이 나부터 지운다.
        // atomic 은 anchor 를 통한 별도의 선택→삭제 경로가 있으니 여기선 inline 만 대상으로 한다.
        if (element.block.getKind() === 'inline' && element.block.isEmpty() === true) {
            const container = element.block.getParent();
            if (container !== null) {
                container.removeBlock(element.block);
                return true;
            }
        }

        if (key === 'Backspace') {
            if (offsets.start !== 0) {
                return false;
            }

            const neighbor = elements[index - 1];

            if (
                neighbor !== undefined &&
                (neighbor.block === element.block || neighbor.block === element.block.getParent()?.block)
            ) {
                neighbor.focus(false);
                return true;
            }

            if (element.block.getKind() === 'atomic') {
                element.block.focus(null, 'before');
                return true;
            }

            const prevSibling = element.block.getAdjacentBlock('prev');
            if (prevSibling === null) {
                return false;
            }

            if (prevSibling.getKind() === 'atomic') {
                prevSibling.focus(null, 'after');
                return true;
            }

            if (prevSibling.getKind() !== 'inline') {
                return false;
            }

            const container = element.block.getParent();
            if (container === null) {
                return false;
            }

            // 앞의 inline 블록이 비어있으면 내 콘텐츠를 옮길 필요가 없다 — 병합 대신 그냥 지운다.
            if (prevSibling.isEmpty() === true) {
                container.removeBlock(prevSibling).then(() => {
                    element.focus(true);
                });
                return true;
            }

            container.mergeBlock(prevSibling, element.block);
            return true;
        }

        const length = element.$getElement().getEl().textContent?.length ?? 0;
        if (offsets.start !== length) {
            return false;
        }

        const neighbor = elements[index + 1];

        if (
            neighbor !== undefined &&
            (neighbor.block === element.block || neighbor.block === element.block.getParent()?.block)
        ) {
            neighbor.focus(true);
            return true;
        }

        // 같은 블록 안엔 더 갈 곳이 없다 — 나 자신이 atomic 이면(예: Quote 의 마지막 필드
        // reference 의 끝) 이웃을 건드리지 말고 내 블록 자신의 anchor 로 들어간다.
        if (element.block.getKind() === 'atomic') {
            element.block.focus(null, 'after');
            return true;
        }

        // Backspace 쪽과 대칭 — 콘텐츠(CE) 유무가 아니라 DOM 상 실제 형제블록으로 판단한다.
        const nextSibling = element.block.getAdjacentBlock('next');
        if (nextSibling === null) {
            return false;
        }

        if (nextSibling.getKind() === 'atomic') {
            nextSibling.focus(null, 'before');
            return true;
        }

        if (nextSibling.getKind() !== 'inline') {
            return false;
        }

        const container = element.block.getParent();
        if (container === null) {
            return false;
        }

        // Backspace 쪽과 대칭 — 뒤의 inline 블록이 비어있으면 병합 대신 그냥 지운다.
        if (nextSibling.isEmpty() === true) {
            container.removeBlock(nextSibling).then(() => {
                element.focus(false);
            });
            return true;
        }

        container.mergeBlock(element.block, nextSibling);
        return true;
    }

    /**
     * 에디터 컨테이너에 포커스가 들어왔을 때 처리한다.
     *
     * @param e - focusin 이벤트
     */
    onFocus(e: FocusEvent): void {
        const element = this.editor.focus.resolveElement(e.target);
        if (element !== null) {
            this.editor.focus.setElement(element);
            return;
        }

        const [nearest] = this.editor.focus.resolveContainer(e.target);
        if (nearest !== undefined) {
            this.editor.focus.setBlock(nearest);
            nearest.onFocusable();
            return;
        }

        const block = this.editor.focus.resolveBlock(e.target);
        if (block !== null) {
            this.editor.focus.setBlock(block);
        }
    }

    /**
     * 에디터 컨테이너를 완전히 벗어났을 때만 처리한다.
     *
     * @param e - focusout 이벤트
     */
    onBlur(e: FocusEvent): void {
        if (
            this.editor
                .$getContainer()
                .getEl()
                .contains(e.relatedTarget as Node) == true
        ) {
            return;
        }

        if (this.editor.focus.isSuspended() === true) {
            return;
        }

        this.editor.focus.setBlock(null);
    }

    /**
     * 메뉴 이벤트를 처리한다.
     *
     * @param e - contextmenu 이벤트
     */
    onMenu(e: MouseEvent): void {
        throw new Error(
            '구현 예정 — v1 Editor.showContextMenu 이관 + Block.onMenu(menu, e) 체인 순회로 교체: ' +
                'const menu = this.editor.getMenu(); e.target 에서 가장 가까운 [data-block] 찾아 ' +
                'let block = 그 Block; while (true) { if (block.onMenu(menu, e)) break; ' +
                'if (block === this.editor.root) break; block = block.getParent().block; } ' +
                '공통 항목(블록 삭제 등) 추가 후 menu.show(e)'
        );
    }

    /**
     * 파괴적 블록수정이 예상될 경우 블록정리를 준비한다.
     */
    #prepareDestructiveCleanup(): void {
        if (this.#pendingDestructiveCleanup !== null) {
            return;
        }

        const range = this.editor.selection.getRange();
        if (range === null) {
            return;
        }

        const startElement = this.editor.focus.resolveElement(range.startContainer);
        const startBlock = startElement?.block ?? this.editor.focus.resolveBlock(range.startContainer);
        const endBlock =
            this.editor.focus.resolveElement(range.endContainer)?.block ??
            this.editor.focus.resolveBlock(range.endContainer);
        if (startBlock === null || endBlock === null) {
            return;
        }

        const selectedBlocks = this.editor.selection.getSelectedBlocks();

        const containers = new Set<Container>();
        const startContainer = startBlock.getParent();
        const endContainer = endBlock.getParent();
        if (startContainer !== null) {
            containers.add(startContainer);
        }
        if (endContainer !== null) {
            containers.add(endContainer);
        }
        for (const block of selectedBlocks) {
            const container = block.getParent();
            if (container !== null) {
                containers.add(container);
            }
        }

        for (const container of containers) {
            container.onBeforeEdit();
        }

        // 네이티브 삭제가 실제로 살아남는 캐럿 엘리먼트(startElement) 자신의 `input` 리스너를
        // 못 태우고 조상 컨테이너로만 이벤트를 흘려보내는 경우가 있다(`onDeleteBoundary`의
        // 동일한 우회책 참고, 실측 확인) — 삭제 전에 미리 "곧 편집될 것"을 예고해두면,
        // `#finishDestructiveCleanup()`이 `checkSynced()`로 실제 반영 여부를 확인해 못
        // 탔을 때만 직접 동기화(빈 상태 반영)한다.
        if (startElement instanceof Text) {
            startElement.expectEdit();
        }

        this.#pendingDestructiveCleanup = {
            containers: [...containers],
            selectedBlockIds: selectedBlocks.map((block) => block.getId()),
        };
    }

    /**
     * 파괴적 병합이 일어난 후 블록정리 및 히스토리를 기록한다.
     */
    #finishDestructiveCleanup(pending: { containers: Container[]; selectedBlockIds: string[] }): void {
        const range = this.editor.selection.getRange();
        const caretElement = range !== null ? this.editor.focus.resolveElement(range.startContainer) : null;
        const caretBlock = caretElement?.block ?? null;

        for (const id of pending.selectedBlockIds) {
            const $block = Html.get(`[data-block="${id}"]`, this.editor.root.body.$getElement());
            if ($block === null) {
                continue;
            }

            const block = $block.getData('block') as Block;
            if (block === caretBlock) {
                if (block.getKind() === 'atomic') {
                    this.convertAtomicRemnantToParagraph(block, caretElement);
                }
                continue;
            }

            $block.getEl().remove();
        }

        if (caretElement instanceof Text) {
            caretElement.checkSynced();
        }

        for (const container of pending.containers) {
            container.onEdit();
        }
    }

    /**
     * inline 블록이 아닌 엘리먼트에 키입력이 되었을 때, 해당 블록을 본문블록으로 변경한다.
     *
     * `#finishDestructiveCleanup()`(Backspace/Delete)과 `CopyPaste`(붙여넣기)가 같이 쓰므로
     * private 로 숨기지 않는다.
     */
    convertAtomicRemnantToParagraph(block: Block, composingElement: Element | null): void {
        if (composingElement === null || composingElement.block !== block) {
            block.$getBlock().getEl().remove();
            (document.activeElement as HTMLElement | null)?.blur();
            return;
        }

        const container = block.getParent();
        if (container === null || container.blocks.includes('paragraph') === false) {
            block.$getBlock().getEl().remove();
            (document.activeElement as HTMLElement | null)?.blur();
            return;
        }

        const $blockDom = block.$getBlock();
        const blockEl = $blockDom.getEl() as RootinBlock;
        const composingDomEl = composingElement.$getElement().getEl();

        for (const child of [...blockEl.children]) {
            if (child !== composingDomEl) {
                child.remove();
            }
        }

        const paragraph = new Paragraph(this.editor);
        paragraph.$block = $blockDom;
        paragraph.text = composingElement as Text;
        composingElement.block = paragraph;
        composingElement.name = 'text';

        blockEl.setBlock(paragraph);
        $blockDom.setData('block', paragraph, false);
        $blockDom.setAttr('data-block', paragraph.getId());
        $blockDom.setAttr('data-type', paragraph.getType());
        $blockDom.setAttr('data-kind', paragraph.getKind());
        composingElement.$getElement().setAttr('data-name', 'text');

        Language.resolveText(paragraph.placeholder).then((text) => {
            composingDomEl.setAttribute('data-placeholder', text);
        });
    }
}
